
import React, { useEffect } from "react";
import styled from "styled-components";
import ToDoApi from "../../API/ToDoApi";

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1500;

    display: flex;
    justify-content: center;
    align-items: center;

    padding: 20px;
    background: rgba(0, 0, 0, 0.7);
`;

const Modal = styled.div`
    width: 100%;
    max-width: 850px;
    max-height: 92vh;

    overflow-y: auto;
    box-sizing: border-box;

    background: white;
    border-radius: 16px;
    padding: 24px;

    box-shadow: 0 12px 40px rgba(0,0,0,0.25);

    @media (max-width: 600px) {
        padding: 15px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 18px;
`;

const Title = styled.h2`
    margin: 0;
    color: #166534;
    font-size: 21px;
`;

const CloseButton = styled.button`
    border: none;
    background: #f1f5f9;
    border-radius: 8px;
    width: 36px;
    height: 36px;
    font-size: 22px;
    cursor: pointer;
`;

const InfoRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
    font-size: 13px;
    color: #64748b;
`;

const Section = styled.div`
    margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
    font-size: 14px;
    color: #166534;
    margin: 0 0 10px;
`;

const Comment = styled.div`
    padding: 14px;
    border-radius: 9px;
    background: #f8faf9;

    font-size: 14px;
    line-height: 1.6;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
`;

const Gallery = styled.div`
    display: grid;
    grid-template-columns: repeat(
        auto-fill,
        minmax(min(100%, 180px), 1fr)
    );
    gap: 10px;
`;

const Photo = styled.img`
    width: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;

    border-radius: 9px;
    cursor: pointer;
    border: 1px solid #e2e8e5;

    &:hover {
        border-color: #019A32;
    }
`;

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 24px;
`;

const Button = styled.button`
    padding: 10px 16px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;

    background: ${({ $secondary }) =>
    $secondary ? "white" : "#019A32"};

    color: ${({ $secondary }) =>
    $secondary ? "#166534" : "white"};

    border: 1px solid ${({ $secondary }) =>
    $secondary ? "#d1d5db" : "#019A32"};

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

function formatDate(date) {
    if (!date) return "";

    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(
        String(date)
    );

    return match
        ? `${match[3]}.${match[2]}.${match[1]}`
        : String(date);
}

const TaskDetailsModal = ({
                              task,
                              busy,
                              onClose,
                              onComplete,
                              onDelete,
                              onPreview
                          }) => {

    useEffect(() => {
        if (!task) return;

        const handleKeyDown = event => {
            if (event.key === "Escape") onClose();
        };

        window.addEventListener("keydown", handleKeyDown);

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [task, onClose]);

    if (!task) return null;

    const photos = task.photos ?? [];
    const completionPhotos = task.completionPhotos ?? [];

    return (
        <Overlay onClick={onClose}>
            <Modal
                role="dialog"
                aria-modal="true"
                aria-label="Aufgabendetails"
                onClick={e => e.stopPropagation()}
            >
                <Header>
                    <Title>Aufgabe – {task.house}</Title>

                    <CloseButton
                        type="button"
                        onClick={onClose}
                        aria-label="Schließen"
                    >
                        ×
                    </CloseButton>
                </Header>

                <InfoRow>
                    <span>Haus: {task.house}</span>
                    <span>Etage: {task.floor || "–"}</span>
                    <span>Von: {task.author}</span>
                    <span>Datum: {formatDate(task.date)}</span>
                    <span>
                         Status: {task.completed ? "Erledigt" : "Offen"}
                     </span>
                </InfoRow>

                <Section>
                    <SectionTitle>Aufgabe</SectionTitle>
                    <Comment>{task.comment}</Comment>
                </Section>

                <Section>
                    <SectionTitle>
                        Fotos vorher ({photos.length})
                    </SectionTitle>

                    <Gallery>
                        {photos.map(photo => (
                            <Photo
                                key={photo.id}
                                src={ToDoApi.getPhotoUrl(photo)}
                                alt="Vorher"
                                onClick={() =>
                                    onPreview(
                                        ToDoApi.getPhotoUrl(photo)
                                    )
                                }
                            />
                        ))}
                    </Gallery>
                </Section>

                {task.completed && (
                    <>
                        <Section>
                            <SectionTitle>
                                Kommentar zur Erledigung
                            </SectionTitle>

                            <Comment>
                                {task.completionComment ||
                                    "Kein Kommentar"}
                            </Comment>
                        </Section>

                        <Section>
                            <SectionTitle>
                                Fotos nachher ({completionPhotos.length})
                            </SectionTitle>

                            <Gallery>
                                {completionPhotos.map(photo => (
                                    <Photo
                                        key={photo.id}
                                        src={ToDoApi.getPhotoUrl(photo)}
                                        alt="Nachher"
                                        onClick={() =>
                                            onPreview(
                                                ToDoApi.getPhotoUrl(photo)
                                            )
                                        }
                                    />
                                ))}
                            </Gallery>
                        </Section>
                    </>
                )}

                <Actions>
                    <Button
                        type="button"
                        $secondary
                        disabled={busy}
                        onClick={() => onDelete(task)}
                    >
                        Löschen
                    </Button>

                    <Button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                            onComplete(task);
                            onClose();
                        }}
                    >
                        {task.completed
                            ? "Wieder öffnen"
                            : "✓ Erledigen"}
                    </Button>
                </Actions>
            </Modal>
        </Overlay>
    );
};

export default TaskDetailsModal;
